import { makeAutoObservable } from "mobx";
import moment from "moment";

import type { TFieldTrackDevice } from "@src/services/fieldsService";

/*
 * "When was this harvested?" — one worked day of a field's tracks, coloured
 * and filtered by time of day. Shared by FieldsScreenVM (main pane) and
 * CompareVM (right pane) so yield-left / time-right read the same.
 *
 * Times are unix seconds throughout, like DeviceStatisticsResponse.getTimeList.
 */

/** Start/stop snap: customers correlate with their own per-second logs. */
export const TIME_STEP_S = 10;

/** An idle stretch longer than this (lunch, refuelling, overnight) collapses
 *  on the time axis, so the slider spends its width on time actually worked. */
const GAP_S = 20 * 60;

/** Activity histogram resolution along the axis. */
const BINS = 48;

export type TTimeSegment = { from: number; to: number };

/**
 * The time axis of one day: the worked spans laid end to end, each idle gap
 * squeezed to `gapWidth`. Axis positions are in seconds too (0..axisLength),
 * and every segment edge sits on a TIME_STEP_S boundary, so an axis position
 * on the step grid maps back to a time on the step grid.
 */
export type TTrackTimeline = {
  start: number;
  end: number;
  segments: TTimeSegment[];
  /** Axis offset of each segment's start. */
  offsets: number[];
  gapWidth: number;
  axisLength: number;
  /** Fixes per axis bin, every machine together. */
  bins: number[];
};

const floorStep = (t: number) => Math.floor(t / TIME_STEP_S) * TIME_STEP_S;
const ceilStep = (t: number) => Math.ceil(t / TIME_STEP_S) * TIME_STEP_S;

/** Every fix time of the given machines, sorted. Null when there are none. */
function sortedTimes(devices: TFieldTrackDevice[]): number[] {
  const times: number[] = [];
  for (const d of devices) {
    for (const row of d.tracks ?? []) {
      const t = Date.parse(String(row.time));
      if (Number.isFinite(t)) times.push(Math.floor(t / 1000));
    }
  }
  return times.sort((a, b) => a - b);
}

export function buildTrackTimeline(devices: TFieldTrackDevice[]): TTrackTimeline | null {
  const times = sortedTimes(devices);
  if (times.length === 0) return null;

  const segments: TTimeSegment[] = [];
  let from = times[0];
  for (let i = 1; i < times.length; i++) {
    if (times[i] - times[i - 1] > GAP_S) {
      segments.push({ from: floorStep(from), to: ceilStep(times[i - 1]) });
      from = times[i];
    }
  }
  segments.push({ from: floorStep(from), to: ceilStep(times[times.length - 1]) });
  // A single fix would make a zero-length axis.
  for (const s of segments) if (s.to <= s.from) s.to = s.from + TIME_STEP_S;

  const active = segments.reduce((sum, s) => sum + (s.to - s.from), 0);
  const gapWidth = ceilStep(Math.max(60, active * 0.03));
  const offsets: number[] = [];
  let offset = 0;
  for (const s of segments) {
    offsets.push(offset);
    offset += s.to - s.from + gapWidth;
  }
  const axisLength = offset - gapWidth;

  const timeline: TTrackTimeline = {
    start: segments[0].from,
    end: segments[segments.length - 1].to,
    segments,
    offsets,
    gapWidth,
    axisLength,
    bins: new Array(BINS).fill(0),
  };
  for (const t of times) {
    const bin = Math.min(BINS - 1, Math.floor((timeToAxis(timeline, t) / axisLength) * BINS));
    timeline.bins[bin]++;
  }
  return timeline;
}

/** Axis position of a time. Inside a collapsed gap it is proportional, so
 *  colours and ticks stay monotonic. */
export function timeToAxis(tl: TTrackTimeline, t: number): number {
  const { segments, offsets } = tl;
  if (t <= segments[0].from) return 0;
  for (let i = 0; i < segments.length; i++) {
    const s = segments[i];
    if (t <= s.to) return offsets[i] + Math.max(0, t - s.from);
    const next = segments[i + 1];
    if (next && t < next.from) {
      return offsets[i] + (s.to - s.from) + (tl.gapWidth * (t - s.to)) / (next.from - s.to);
    }
  }
  return tl.axisLength;
}

/** Time at an axis position. A position inside a collapsed gap snaps to the
 *  nearer working edge — there is nothing to select in between. */
export function axisToTime(tl: TTrackTimeline, p: number): number {
  const { segments, offsets } = tl;
  for (let i = 0; i < segments.length; i++) {
    const s = segments[i];
    const len = s.to - s.from;
    if (p <= offsets[i] + len) return floorStep(s.from + Math.max(0, p - offsets[i]));
    if (i + 1 < segments.length && p < offsets[i + 1]) {
      return p - (offsets[i] + len) < tl.gapWidth / 2 ? s.to : segments[i + 1].from;
    }
  }
  return tl.end;
}

/**
 * Viridis, dark purple (first worked) to yellow (last). Deliberately not the
 * red-to-green sensor ramp: this view sits next to the yield map, and a red
 * swath beside a red hex must never read as "low yield".
 */
const TIME_STOPS: Array<[number, number, number]> = [
  [68, 1, 84],
  [72, 40, 120],
  [62, 74, 137],
  [49, 104, 142],
  [38, 130, 142],
  [31, 158, 137],
  [53, 183, 121],
  [109, 205, 89],
  [180, 222, 44],
  [253, 231, 37],
];

export function timeRampColor(fraction: number): string {
  const f = Math.min(Math.max(Number.isFinite(fraction) ? fraction : 0, 0), 1);
  const x = f * (TIME_STOPS.length - 1);
  const i = Math.min(Math.floor(x), TIME_STOPS.length - 2);
  const t = x - i;
  const a = TIME_STOPS[i];
  const b = TIME_STOPS[i + 1];
  const c = (k: number) => Math.round(a[k] + (b[k] - a[k]) * t);
  return `rgb(${c(0)}, ${c(1)}, ${c(2)})`;
}

/** The ramp left to right — the time slider's track. Colour runs along the
 *  compressed axis, so the whole ramp is spent on worked time. */
export const TIME_RAMP_GRADIENT = `linear-gradient(to right, ${TIME_STOPS.map(
  (c, i) => `rgb(${c[0]}, ${c[1]}, ${c[2]}) ${(i / (TIME_STOPS.length - 1)) * 100}%`
).join(", ")})`;

/** Swath colour for a fix time on this day's axis. */
export const timelineColor = (tl: TTrackTimeline, t: number): string =>
  timeRampColor(tl.axisLength > 0 ? timeToAxis(tl, t) / tl.axisLength : 0);

const TICK_STEPS_MIN = [5, 10, 15, 30, 60, 120, 180, 240, 360];

/**
 * Tick marks on round local times (14:00, 14:30…), thinned so labels never
 * collide. `fraction` is the position along the axis, 0..1.
 */
export function timelineTicks(
  tl: TTrackTimeline,
  maxTicks: number
): Array<{ fraction: number; label: string }> {
  const span = tl.axisLength;
  const minStepS = span / Math.max(1, maxTicks);
  const stepS = (TICK_STEPS_MIN.find((m) => m * 60 >= minStepS) ?? 360) * 60;
  const minGap = 1 / (maxTicks + 1);

  const ticks: Array<{ fraction: number; label: string }> = [];
  for (const s of tl.segments) {
    // Align on local wall-clock time, not UTC: a tick reads "14:00" locally.
    const offsetS = new Date(s.from * 1000).getTimezoneOffset() * 60;
    for (
      let wall = Math.ceil((s.from - offsetS) / stepS) * stepS;
      wall + offsetS <= s.to;
      wall += stepS
    ) {
      const t = wall + offsetS;
      const fraction = timeToAxis(tl, t) / span;
      const prev = ticks[ticks.length - 1];
      if (prev && fraction - prev.fraction < minGap) continue;
      ticks.push({ fraction, label: moment(t * 1000).format("HH:mm") });
    }
  }
  return ticks;
}

/** Axis fractions where a collapsed gap sits, to mark the break. */
export const timelineBreaks = (tl: TTrackTimeline): number[] =>
  tl.segments
    .slice(0, -1)
    .map((s, i) => (tl.offsets[i] + (s.to - s.from) + tl.gapWidth / 2) / tl.axisLength);

/** The browser's zone name for a moment ("CDT", "GMT+2") — times are local. */
export function timeZoneLabel(t: number): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(
      new Date(t * 1000)
    );
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/** "HH:mm:ss" for an `<input type="time" step="10">`. */
export const toTimeInput = (t: number): string => moment(t * 1000).format("HH:mm:ss");

/**
 * Read a typed "HH:mm[:ss]" back onto the axis. Days are local, but the
 * axis snaps outward to 10 s and a DST night can blur its edge, so the clock
 * time is tried on each local date the axis touches and the reading nearest
 * `near` wins. Null if unparseable.
 */
export function fromTimeInput(tl: TTrackTimeline, value: string, near: number): number | null {
  if (!/^\d{1,2}:\d{2}(:\d{2})?$/.test(value)) return null;
  const dates = new Set([
    moment(tl.start * 1000).format("YYYY-MM-DD"),
    moment(tl.end * 1000).format("YYYY-MM-DD"),
  ]);
  let best: number | null = null;
  for (const date of dates) {
    const m = moment(`${date} ${value}`, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm"], true);
    if (!m.isValid()) continue;
    const t = Math.min(Math.max(floorStep(m.unix()), tl.start), tl.end);
    if (best == null || Math.abs(t - near) < Math.abs(best - near)) best = t;
  }
  return best;
}

/** What the tracks layer needs to draw a focused day: the visible window and
 *  how to colour a fix time. */
export type TTrackTimeRender = {
  from: number;
  to: number;
  colorForTime: (t: number) => string;
};

/**
 * One pane's "focused day": which worked day is being read by time, and the
 * start/stop window on it. Null day = every day, drawn as before.
 *
 * `devicesFor` must read something observable (each VM's `trackDevices`) so
 * the timeline recomputes when the focused day's points arrive.
 */
export class TrackTimeFocus {
  day: string | null = null;
  /** Null = the whole day. */
  window: TTimeSegment | null = null;

  constructor(private devicesFor: (day: string) => TFieldTrackDevice[]) {
    makeAutoObservable<TrackTimeFocus, "devicesFor">(this, { devicesFor: false });
  }

  /** Null until the focused day's points have loaded. */
  get timeline(): TTrackTimeline | null {
    return this.day ? buildTrackTimeline(this.devicesFor(this.day)) : null;
  }

  get from(): number | null {
    return this.window?.from ?? this.timeline?.start ?? null;
  }

  get to(): number | null {
    return this.window?.to ?? this.timeline?.end ?? null;
  }

  /** Clicking the focused day again goes back to every day. */
  toggle = (day: string) => {
    this.day = this.day === day ? null : day;
    this.window = null;
  };

  clear = () => {
    this.day = null;
    this.window = null;
  };

  setWindow = (from: number, to: number) => {
    const tl = this.timeline;
    if (!tl) return;
    const lo = Math.max(tl.start, Math.min(from, to));
    const hi = Math.min(tl.end, Math.max(from, to));
    this.window = lo <= tl.start && hi >= tl.end ? null : { from: lo, to: hi };
  };

  get render(): TTrackTimeRender | null {
    const tl = this.timeline;
    if (!tl) return null;
    return {
      from: this.window?.from ?? tl.start,
      to: this.window?.to ?? tl.end,
      colorForTime: (t) => timelineColor(tl, t),
    };
  }
}
